import { useCallback, useEffect, useState } from 'react';
import { isArrowDownPressed, isArrowUpPressed, isEnterPressed } from 'utils';

type UseListKeyboardNavigationOptions = {
  itemsCount: number;
  loop?: boolean;
  showResults: boolean;
  initialIndex?: number;
  onSelect?: (index: number) => void;
};

const NEXT_INDEX_OFFSET = 1;
const PREV_INDEX_OFFSET = -1;

const useListKeyboardNavigation = ({
  itemsCount,
  showResults,
  loop = true,
  initialIndex = PREV_INDEX_OFFSET,
  onSelect,
}: UseListKeyboardNavigationOptions) => {
  const [activeIndex, setActiveIndex] = useState<number>(initialIndex);

  useEffect(() => {
    if (!itemsCount) {
      setActiveIndex(PREV_INDEX_OFFSET);
      return;
    }

    if (activeIndex >= itemsCount) {
      setActiveIndex(itemsCount - 1);
    }
  }, [activeIndex, itemsCount]);

  const getNextIndex = useCallback(
    (delta: number) => {
      if (!itemsCount) return PREV_INDEX_OFFSET;

      const lastIndex = itemsCount - 1;
      const nextIndex =
        activeIndex < 0 ? (delta > 0 ? 0 : lastIndex) : activeIndex + delta;

      if (loop) {
        if (nextIndex > lastIndex) return 0;
        if (nextIndex < 0) return lastIndex;
      }

      return Math.min(Math.max(nextIndex, 0), lastIndex);
    },
    [activeIndex, itemsCount, loop],
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (!itemsCount) return;

      if (isEnterPressed(event)) {
        if (showResults) {
          event.stopPropagation();
          event.preventDefault();
        }

        if (activeIndex >= 0) {
          onSelect?.(activeIndex);
        }
      }

      if (isArrowDownPressed(event)) {
        event.preventDefault();
        setActiveIndex(getNextIndex(NEXT_INDEX_OFFSET));
      }

      if (isArrowUpPressed(event)) {
        event.preventDefault();
        setActiveIndex(getNextIndex(PREV_INDEX_OFFSET));
      }
    },
    [activeIndex, showResults, itemsCount, getNextIndex, onSelect],
  );

  const onMouseEnter = useCallback(
    (index: number) => setActiveIndex(index),
    [],
  );

  return { activeIndex, onKeyDown, onMouseEnter };
};

export default useListKeyboardNavigation;
